import React, { useContext, useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend,
} from 'chart.js';
import apiClient from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/**
 * Gráficas — 3 endpoints reales de `reportes.routes.js#/graficas/*`, todos con firma
 * `/graficas/<nombre>/:fechaInicio/:fechaFin` (`YYYY-MM-DD`) y respuesta
 * `[{Mes|Año, Importe}]` en PESOS (`graficas.service.js` ya regresa `money.aPesos()`).
 *
 * Se evaluó `chart.js` + `react-chartjs-2` (pedido explícito de la tarea): ambos son
 * JS puro del lado del cliente (canvas 2D del NAVEGADOR, sin binding nativo) —
 * `npm install` + `npx vite build` compilaron sin errores ni dependencias nativas
 * (ver resultado del build en el reporte de esta tarea), a diferencia de
 * `chartjs-node-canvas` del backend (ese sí usa `node-canvas`, un módulo nativo de
 * Node — no aplica aquí). No fue necesaria una alternativa ligera.
 */
function hoyISO(offsetDias = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDias);
  return d.toISOString().slice(0, 10);
}

const OPCIONES_BASE = {
  responsive: true,
  plugins: { legend: { display: false } },
  scales: { y: { beginAtZero: true } },
};

function useGrafica(endpoint, fechaInicio, fechaFin, labelKey) {
  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!fechaInicio || !fechaFin) return;
    setCargando(true);
    setError(null);
    apiClient
      .get(`/reportes/graficas/${endpoint}/${fechaInicio}/${fechaFin}`)
      .then(({ data }) => setDatos(data.data || []))
      .catch((e) => { setError(e.message); setDatos([]); })
      .finally(() => setCargando(false));
  }, [endpoint, fechaInicio, fechaFin]);

  return {
    cargando, error,
    chartData: {
      labels: datos.map((d) => d[labelKey]),
      datasets: [{ label: 'Importe', data: datos.map((d) => d.Importe), backgroundColor: '#2563eb' }],
    },
    total: datos.reduce((s, d) => s + (d.Importe || 0), 0),
  };
}

export default function GraficasPage() {
  const { hasPermiso } = useContext(AuthContext);
  const [fechaInicio, setFechaInicio] = useState(hoyISO(-365));
  const [fechaFin, setFechaFin] = useState(hoyISO());

  const ventaMensual = useGrafica('venta-mensual', fechaInicio, fechaFin, 'Mes');
  const ventaAnual = useGrafica('venta-anual', fechaInicio, fechaFin, 'Año');
  const abonoMensual = useGrafica('abono-mensual', fechaInicio, fechaFin, 'Mes');

  const puedeVer = hasPermiso('reportes:ver');
  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  const money = (n) => `$${Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-slate-900">Gráficas</h1>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="label">Fecha inicio</label>
          <input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} className="input-base" />
        </div>
        <div>
          <label className="label">Fecha fin</label>
          <input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} className="input-base" />
        </div>
      </div>

      {[
        { titulo: 'Venta mensual (Cargos)', g: ventaMensual },
        { titulo: 'Venta anual (Cargos)', g: ventaAnual },
        { titulo: 'Abono mensual', g: abonoMensual },
      ].map(({ titulo, g }) => (
        <div key={titulo} className="card p-4 space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="font-medium text-slate-900">{titulo}</h2>
            <span className="text-sm text-slate-500">Total: {money(g.total)}</span>
          </div>
          {g.error && <p className="text-sm text-danger-600">{g.error}</p>}
          {g.cargando ? (
            <p className="text-sm text-slate-400">Cargando...</p>
          ) : (
            <Bar data={g.chartData} options={OPCIONES_BASE} />
          )}
        </div>
      ))}
    </div>
  );
}
